import { useState } from 'react'
import { toast } from 'sonner'

import { Spinner } from '@/components/ui/spinner'
import { Handler } from '../lib/typeHelper'
import { syncEnvelope, useEnvelopeSignUrl } from '../models/esign'
import { EsignEnvelope } from './EsignEnvelope'

interface EsignProps {
  title: React.ReactNode
  onSign: Handler
  envelopeId: string | undefined
}

export function Esign({ title, onSign, envelopeId }: EsignProps) {
  const [isSigned, setIsSigned] = useState(false)
  const { data, isError } = useEnvelopeSignUrl(envelopeId)

  if (!envelopeId || isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 text-center">
        <p className="text-md text-red-600">We could not load your document. Please refresh and try again.</p>
      </div>
    )
  }

  if (isSigned) {
    return (
      <div>
        <h1 className="mb-4 text-center text-2xl font-bold">{title}</h1>
        <div className="mb-4 text-center">Thank you for signing. You can close the browser now.</div>
      </div>
    )
  }

  if (!data) {
    return <Spinner className="mx-auto my-auto size-10" />
  }

  async function handleSign() {
    try {
      syncEnvelope(envelopeId!)
      setIsSigned(true)
      onSign()
    } catch {
      toast.error('Something went wrong. Please try again.')
    }
  }

  return (
    <EsignFullScreenWrap title={title ? <div className={`mb-4 text-center`}>{title}</div> : null}>
      <EsignEnvelope
        url={data.signUrl}
        onAgree={handleSign}
        onError={() => toast.error('Something went wrong. Please try again.')}
      />
    </EsignFullScreenWrap>
  )
}

interface EsignFullScreenWrapProps {
  title: React.ReactNode
  children: React.ReactNode
  description?: React.ReactNode
}

function EsignFullScreenWrap({ children, title, description }: EsignFullScreenWrapProps) {
  return (
    <div className="flex h-full flex-1 flex-col">
      <h1 className="mb-4 text-center text-2xl font-bold">{title}</h1>
      {description}
      <div className="flex-1">{children}</div>
      <style>{`.content { display: flex; flex-direction: column; }`}</style>
    </div>
  )
}
